import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAnalytics } from './useAnalytics';
import { useBattlePass, useSubscription } from './useBattlePass';

// ============================================
// MISSION TYPES
// ============================================

export type MissionType =
  | 'play_matches'
  | 'win_matches'
  | 'complete_waves'
  | 'defeat_enemies'
  | 'defeat_bosses'
  | 'collect_powerups'
  | 'earn_chips'
  | 'use_items'
  | 'play_multiplayer'
  | 'reach_wave'
  | 'deal_damage'
  | 'survive_time'
  | 'no_damage_wave'
  | 'perfect_wave';

export type MissionDifficulty = 'easy' | 'medium' | 'hard';
export type MissionCategory = 'daily' | 'weekly' | 'special';

export interface MissionReward {
  type: 'chips' | 'crystals' | 'xp' | 'item';
  amount: number;
  itemId?: string;
}

export interface Mission {
  id: string;
  type: MissionType;
  category: MissionCategory;
  difficulty: MissionDifficulty;
  title: string;
  description: string;
  icon: string;
  target: number;
  progress: number;
  rewards: MissionReward[];
  completed: boolean;
  claimed: boolean;
  expiresAt: number;
  createdAt: number;
}

// ============================================
// MISSION TEMPLATES
// ============================================

interface MissionTemplate {
  type: MissionType;
  title: string;
  description: string;
  icon: string;
  targets: { easy: number; medium: number; hard: number };
  rewards: { easy: MissionReward[]; medium: MissionReward[]; hard: MissionReward[] };
}

const MISSION_TEMPLATES: MissionTemplate[] = [
  {
    type: 'play_matches',
    title: 'Jogador Ativo',
    description: 'Complete {target} partidas',
    icon: '🎮',
    targets: { easy: 3, medium: 5, hard: 10 },
    rewards: {
      easy: [{ type: 'chips', amount: 200 }, { type: 'xp', amount: 150 }],
      medium: [{ type: 'chips', amount: 400 }, { type: 'xp', amount: 300 }],
      hard: [{ type: 'chips', amount: 800 }, { type: 'chips', amount: 500 }, { type: 'xp', amount: 500 }],
    },
  },
  {
    type: 'win_matches',
    title: 'Vitorioso',
    description: 'Vença {target} partidas',
    icon: '🏆',
    targets: { easy: 1, medium: 3, hard: 5 },
    rewards: {
      easy: [{ type: 'chips', amount: 300 }, { type: 'xp', amount: 200 }],
      medium: [{ type: 'chips', amount: 600 }, { type: 'xp', amount: 400 }],
      hard: [{ type: 'chips', amount: 1000 }, { type: 'chips', amount: 1000 }, { type: 'xp', amount: 600 }],
    },
  },
  {
    type: 'complete_waves',
    title: 'Sobrevivente',
    description: 'Complete {target} waves',
    icon: '🌊',
    targets: { easy: 10, medium: 25, hard: 50 },
    rewards: {
      easy: [{ type: 'chips', amount: 250 }, { type: 'xp', amount: 150 }],
      medium: [{ type: 'chips', amount: 500 }, { type: 'xp', amount: 350 }],
      hard: [{ type: 'chips', amount: 1000 }, { type: 'chips', amount: 600 }, { type: 'xp', amount: 500 }],
    },
  },
  {
    type: 'defeat_enemies',
    title: 'Destruidor',
    description: 'Derrote {target} inimigos',
    icon: '💥',
    targets: { easy: 25, medium: 50, hard: 100 },
    rewards: {
      easy: [{ type: 'chips', amount: 200 }, { type: 'xp', amount: 150 }],
      medium: [{ type: 'chips', amount: 450 }, { type: 'xp', amount: 300 }],
      hard: [{ type: 'chips', amount: 900 }, { type: 'chips', amount: 400 }, { type: 'xp', amount: 500 }],
    },
  },
  {
    type: 'defeat_bosses',
    title: 'Caçador de Chefes',
    description: 'Derrote {target} bosses',
    icon: '👹',
    targets: { easy: 1, medium: 3, hard: 5 },
    rewards: {
      easy: [{ type: 'chips', amount: 400 }, { type: 'xp', amount: 250 }],
      medium: [{ type: 'chips', amount: 800 }, { type: 'chips', amount: 600 }, { type: 'xp', amount: 450 }],
      hard: [{ type: 'chips', amount: 1500 }, { type: 'chips', amount: 1500 }, { type: 'xp', amount: 750 }],
    },
  },
  {
    type: 'collect_powerups',
    title: 'Colecionador',
    description: 'Colete {target} power-ups',
    icon: '⚡',
    targets: { easy: 5, medium: 15, hard: 30 },
    rewards: {
      easy: [{ type: 'chips', amount: 150 }, { type: 'xp', amount: 100 }],
      medium: [{ type: 'chips', amount: 350 }, { type: 'xp', amount: 250 }],
      hard: [{ type: 'chips', amount: 700 }, { type: 'xp', amount: 400 }],
    },
  },
  {
    type: 'earn_chips',
    title: 'Rico',
    description: 'Ganhe {target} chips',
    icon: '💰',
    targets: { easy: 500, medium: 1500, hard: 3000 },
    rewards: {
      easy: [{ type: 'chips', amount: 200 }, { type: 'xp', amount: 150 }],
      medium: [{ type: 'chips', amount: 500 }, { type: 'xp', amount: 300 }],
      hard: [{ type: 'chips', amount: 1000 }, { type: 'xp', amount: 500 }],
    },
  },
  {
    type: 'play_multiplayer',
    title: 'Social',
    description: 'Jogue {target} partidas multiplayer',
    icon: '👥',
    targets: { easy: 1, medium: 3, hard: 5 },
    rewards: {
      easy: [{ type: 'chips', amount: 300 }, { type: 'xp', amount: 200 }],
      medium: [{ type: 'chips', amount: 700 }, { type: 'chips', amount: 400 }, { type: 'xp', amount: 400 }],
      hard: [{ type: 'chips', amount: 1200 }, { type: 'chips', amount: 1000 }, { type: 'xp', amount: 600 }],
    },
  },
  {
    type: 'reach_wave',
    title: 'Veterano',
    description: 'Alcance a wave {target} em uma partida',
    icon: '🎯',
    targets: { easy: 5, medium: 10, hard: 20 },
    rewards: {
      easy: [{ type: 'chips', amount: 250 }, { type: 'xp', amount: 150 }],
      medium: [{ type: 'chips', amount: 600 }, { type: 'xp', amount: 350 }],
      hard: [{ type: 'chips', amount: 1200 }, { type: 'chips', amount: 800 }, { type: 'xp', amount: 600 }],
    },
  },
  {
    type: 'no_damage_wave',
    title: 'Intocável',
    description: 'Complete {target} waves sem tomar dano',
    icon: '🛡️',
    targets: { easy: 1, medium: 3, hard: 5 },
    rewards: {
      easy: [{ type: 'chips', amount: 400 }, { type: 'xp', amount: 250 }],
      medium: [{ type: 'chips', amount: 800 }, { type: 'chips', amount: 600 }, { type: 'xp', amount: 450 }],
      hard: [{ type: 'chips', amount: 1500 }, { type: 'chips', amount: 1500 }, { type: 'xp', amount: 750 }],
    },
  },
];

// Weekly-specific templates
const WEEKLY_TEMPLATES: MissionTemplate[] = [
  {
    type: 'win_matches',
    title: 'Campeão Semanal',
    description: 'Vença {target} partidas esta semana',
    icon: '🥇',
    targets: { easy: 10, medium: 20, hard: 35 },
    rewards: {
      easy: [{ type: 'chips', amount: 2000 }, { type: 'chips', amount: 1000 }, { type: 'xp', amount: 1000 }],
      medium: [{ type: 'chips', amount: 4000 }, { type: 'chips', amount: 2000 }, { type: 'xp', amount: 2000 }],
      hard: [{ type: 'chips', amount: 7500 }, { type: 'chips', amount: 4000 }, { type: 'xp', amount: 3500 }],
    },
  },
  {
    type: 'defeat_enemies',
    title: 'Exterminador',
    description: 'Derrote {target} inimigos esta semana',
    icon: '☠️',
    targets: { easy: 200, medium: 500, hard: 1000 },
    rewards: {
      easy: [{ type: 'chips', amount: 1500 }, { type: 'chips', amount: 800 }, { type: 'xp', amount: 800 }],
      medium: [{ type: 'chips', amount: 3000 }, { type: 'chips', amount: 1600 }, { type: 'xp', amount: 1500 }],
      hard: [{ type: 'chips', amount: 6000 }, { type: 'chips', amount: 3000 }, { type: 'xp', amount: 3000 }],
    },
  },
  {
    type: 'defeat_bosses',
    title: 'Matador de Bosses',
    description: 'Derrote {target} bosses esta semana',
    icon: '🐉',
    targets: { easy: 5, medium: 10, hard: 20 },
    rewards: {
      easy: [{ type: 'chips', amount: 2500 }, { type: 'chips', amount: 1500 }, { type: 'xp', amount: 1200 }],
      medium: [{ type: 'chips', amount: 5000 }, { type: 'chips', amount: 3000 }, { type: 'xp', amount: 2500 }],
      hard: [{ type: 'chips', amount: 10000 }, { type: 'chips', amount: 6000 }, { type: 'xp', amount: 5000 }],
    },
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateMissionId = (): string => {
  return `mission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getEndOfDay = (): number => {
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime();
};

const getEndOfWeek = (): number => {
  const now = new Date();
  const daysUntilSunday = 7 - now.getDay();
  const endOfWeek = new Date(now);
  endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday);
  endOfWeek.setHours(23, 59, 59, 999);
  return endOfWeek.getTime();
};

const pickRandomItems = <T,>(array: T[], count: number): T[] => {
  const shuffled = [...array].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const generateDailyMissions = (count: number = 3): Mission[] => {
  const templates = pickRandomItems(MISSION_TEMPLATES, count);
  const difficulties: MissionDifficulty[] = ['easy', 'medium', 'hard'];
  
  return templates.map((template, index) => {
    // First mission is easy, second is medium, third is hard
    const difficulty = difficulties[Math.min(index, 2)];
    const target = template.targets[difficulty];
    
    return {
      id: generateMissionId(),
      type: template.type,
      category: 'daily' as MissionCategory,
      difficulty,
      title: template.title,
      description: template.description.replace('{target}', target.toString()),
      icon: template.icon,
      target,
      progress: 0,
      rewards: template.rewards[difficulty],
      completed: false,
      claimed: false,
      expiresAt: getEndOfDay(),
      createdAt: Date.now(),
    };
  });
};

const generateWeeklyMissions = (count: number = 3): Mission[] => {
  const templates = pickRandomItems(WEEKLY_TEMPLATES, count);
  const difficulties: MissionDifficulty[] = ['easy', 'medium', 'hard'];
  
  return templates.map((template, index) => {
    const difficulty = difficulties[Math.min(index, 2)];
    const target = template.targets[difficulty];
    
    return {
      id: generateMissionId(),
      type: template.type,
      category: 'weekly' as MissionCategory,
      difficulty,
      title: template.title,
      description: template.description.replace('{target}', target.toString()),
      icon: template.icon,
      target,
      progress: 0,
      rewards: template.rewards[difficulty],
      completed: false,
      claimed: false,
      expiresAt: getEndOfWeek(),
      createdAt: Date.now(),
    };
  });
};

// ============================================
// MISSIONS STORE
// ============================================

interface DailyMissionsState {
  // Missions
  dailyMissions: Mission[];
  weeklyMissions: Mission[];
  
  // Tracking
  lastDailyReset: number;
  lastWeeklyReset: number;
  dailyMissionsCompleted: number;
  weeklyMissionsCompleted: number;
  totalMissionsCompleted: number;
  
  // Streak
  missionStreak: number;
  lastMissionCompletedDate: string;
  
  // Actions
  refreshMissions: () => void;
  updateProgress: (type: MissionType, amount?: number) => void;
  claimReward: (missionId: string) => MissionReward[] | null;
  
  // Getters
  getMission: (id: string) => Mission | undefined;
  getActiveMissions: () => Mission[];
  getCompletedMissions: () => Mission[];
  getClaimableMissions: () => Mission[];
  
  // Stats
  getDailyProgress: () => { completed: number; total: number; claimed: number };
  getWeeklyProgress: () => { completed: number; total: number; claimed: number };
}

export const useDailyMissions = create<DailyMissionsState>()(
  persist(
    (set, get) => ({
      dailyMissions: [],
      weeklyMissions: [],
      lastDailyReset: 0,
      lastWeeklyReset: 0,
      dailyMissionsCompleted: 0,
      weeklyMissionsCompleted: 0,
      totalMissionsCompleted: 0,
      missionStreak: 0,
      lastMissionCompletedDate: '',
      
      refreshMissions: () => {
        const state = get();
        const now = Date.now();
        
        // Check if daily missions need refresh
        const needsDailyRefresh = state.dailyMissions.length === 0 || 
          state.dailyMissions.every(m => m.expiresAt < now);
        
        // Check if weekly missions need refresh
        const needsWeeklyRefresh = state.weeklyMissions.length === 0 ||
          state.weeklyMissions.every(m => m.expiresAt < now);
        
        const updates: Partial<DailyMissionsState> = {};
        
        if (needsDailyRefresh) {
          const extraMissions = useSubscription.getState().getCurrentPlan().benefits.extraMissions || 0;
          updates.dailyMissions = generateDailyMissions(3 + extraMissions);
          updates.lastDailyReset = now;
          
          // Track analytics
          const analytics = useAnalytics.getState();
          analytics.trackEvent('engagement', 'daily_missions_refresh');
        }
        
        if (needsWeeklyRefresh) {
          updates.weeklyMissions = generateWeeklyMissions(3);
          updates.lastWeeklyReset = now;
          
          const analytics = useAnalytics.getState();
          analytics.trackEvent('engagement', 'weekly_missions_refresh');
        }
        
        if (Object.keys(updates).length > 0) {
          set(updates);
        }
      },
      
      updateProgress: (type, amount = 1) => {
        const state = get();
        let updated = false;
        
        const updateMissionProgress = (mission: Mission): Mission => {
          if (mission.type !== type || mission.completed || mission.expiresAt < Date.now()) {
            return mission;
          }
          
          const newProgress = Math.min(mission.progress + amount, mission.target);
          const completed = newProgress >= mission.target;
          
          if (newProgress !== mission.progress) {
            updated = true;
            
            if (completed) {
              // Track completion
              const analytics = useAnalytics.getState();
              analytics.trackEvent('engagement', 'mission_complete', mission.category, undefined, {
                missionId: mission.id,
                missionType: mission.type,
                difficulty: mission.difficulty,
              });
            }
          }
          
          return {
            ...mission,
            progress: newProgress,
            completed,
          };
        };
        
        const newDailyMissions = state.dailyMissions.map(updateMissionProgress);
        const newWeeklyMissions = state.weeklyMissions.map(updateMissionProgress);
        
        if (updated) {
          set({
            dailyMissions: newDailyMissions,
            weeklyMissions: newWeeklyMissions,
          });
        }
      },
      
      claimReward: (missionId) => {
        const state = get();
        
        // Find mission
        let mission = state.dailyMissions.find(m => m.id === missionId);
        let isDaily = true;
        
        if (!mission) {
          mission = state.weeklyMissions.find(m => m.id === missionId);
          isDaily = false;
        }
        
        if (!mission || !mission.completed || mission.claimed) {
          return null;
        }
        
        // Mark as claimed
        const updateMission = (m: Mission) => 
          m.id === missionId ? { ...m, claimed: true } : m;
        
        // Update streak
        const today = new Date().toDateString();
        let newStreak = state.missionStreak;
        
        if (state.lastMissionCompletedDate !== today) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          
          if (state.lastMissionCompletedDate === yesterday.toDateString()) {
            newStreak += 1;
          } else if (state.lastMissionCompletedDate !== today) {
            newStreak = 1;
          }
        }
        
        set({
          dailyMissions: isDaily 
            ? state.dailyMissions.map(updateMission)
            : state.dailyMissions,
          weeklyMissions: !isDaily
            ? state.weeklyMissions.map(updateMission)
            : state.weeklyMissions,
          dailyMissionsCompleted: isDaily 
            ? state.dailyMissionsCompleted + 1 
            : state.dailyMissionsCompleted,
          weeklyMissionsCompleted: !isDaily
            ? state.weeklyMissionsCompleted + 1
            : state.weeklyMissionsCompleted,
          totalMissionsCompleted: state.totalMissionsCompleted + 1,
          missionStreak: newStreak,
          lastMissionCompletedDate: today,
        });
        
        // Add Battle Pass XP
        const battlePass = useBattlePass.getState();
        const xpAmount = isDaily ? 300 : 1000; // Daily = 300 XP, Weekly = 1000 XP
        battlePass.addXP(xpAmount);
        
        // Track
        const analytics = useAnalytics.getState();
        analytics.trackEvent('monetization', 'mission_reward_claim', mission.category, undefined, {
          missionId: mission.id,
          rewards: mission.rewards,
          streak: newStreak,
        });
        
        return mission.rewards;
      },
      
      getMission: (id) => {
        const state = get();
        return state.dailyMissions.find(m => m.id === id) ||
          state.weeklyMissions.find(m => m.id === id);
      },
      
      getActiveMissions: () => {
        const state = get();
        const now = Date.now();
        
        return [
          ...state.dailyMissions.filter(m => !m.claimed && m.expiresAt > now),
          ...state.weeklyMissions.filter(m => !m.claimed && m.expiresAt > now),
        ];
      },
      
      getCompletedMissions: () => {
        const state = get();
        return [
          ...state.dailyMissions.filter(m => m.completed),
          ...state.weeklyMissions.filter(m => m.completed),
        ];
      },
      
      getClaimableMissions: () => {
        const state = get();
        return [
          ...state.dailyMissions.filter(m => m.completed && !m.claimed),
          ...state.weeklyMissions.filter(m => m.completed && !m.claimed),
        ];
      },
      
      getDailyProgress: () => {
        const state = get();
        return {
          completed: state.dailyMissions.filter(m => m.completed).length,
          total: state.dailyMissions.length,
          claimed: state.dailyMissions.filter(m => m.claimed).length,
        };
      },
      
      getWeeklyProgress: () => {
        const state = get();
        return {
          completed: state.weeklyMissions.filter(m => m.completed).length,
          total: state.weeklyMissions.length,
          claimed: state.weeklyMissions.filter(m => m.claimed).length,
        };
      },
    }),
    {
      name: 'rocketanks-daily-missions',
      partialize: (state) => ({
        dailyMissions: state.dailyMissions,
        weeklyMissions: state.weeklyMissions,
        lastDailyReset: state.lastDailyReset,
        lastWeeklyReset: state.lastWeeklyReset,
        dailyMissionsCompleted: state.dailyMissionsCompleted,
        weeklyMissionsCompleted: state.weeklyMissionsCompleted,
        totalMissionsCompleted: state.totalMissionsCompleted,
        missionStreak: state.missionStreak,
        lastMissionCompletedDate: state.lastMissionCompletedDate,
      }),
    }
  )
);

// ============================================
// HOOKS
// ============================================

// Hook to auto-refresh missions on mount
export const useMissionsRefresh = () => {
  const { refreshMissions } = useDailyMissions();
  
  // Refresh on mount
  if (typeof window !== 'undefined') {
    refreshMissions();
  }
  
  return null;
};

// Hook for mission progress tracking
export const useMissionProgress = () => {
  const { updateProgress } = useDailyMissions();
  
  return {
    trackMatchComplete: () => updateProgress('play_matches'),
    trackMatchWin: () => updateProgress('win_matches'),
    trackWaveComplete: () => updateProgress('complete_waves'),
    trackEnemyDefeat: (count: number = 1) => updateProgress('defeat_enemies', count),
    trackBossDefeat: () => updateProgress('defeat_bosses'),
    trackPowerupCollect: () => updateProgress('collect_powerups'),
    trackChipsEarned: (amount: number) => updateProgress('earn_chips', amount),
    trackMultiplayerMatch: () => updateProgress('play_multiplayer'),
    trackWaveReached: (wave: number) => updateProgress('reach_wave', wave),
    trackNoDamageWave: () => updateProgress('no_damage_wave'),
  };
};

// Hook for UI
export const useMissionsUI = () => {
  const state = useDailyMissions();
  
  const dailyProgress = state.getDailyProgress();
  const weeklyProgress = state.getWeeklyProgress();
  const claimable = state.getClaimableMissions();
  
  return {
    dailyMissions: state.dailyMissions,
    weeklyMissions: state.weeklyMissions,
    dailyProgress,
    weeklyProgress,
    claimable,
    claimableCount: claimable.length,
    streak: state.missionStreak,
    claimReward: state.claimReward,
  };
};
